import {
  lazy,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useState,
  Dispatch,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { IPostAttachment } from '@/redux/post/post.d';
import { Button } from '@/components/ui/button';
import { Skeleton } from '../ui/skeleton';
import NotSupportedCarouselItem from './NotSupportedCarouselItem';
import { AttachmentType } from '@/redux/attachment/attachment.d';
import SvgViewer from '@/components/common/SvgViewer';
import { getYouTubeEmbedUrl } from '@/lib/youtube';
import { cn } from '@/lib/utils';

const Viewer = lazy(() => import('@/components/ModelViewer'));

interface PostDetailCarouselProps {
  attachments: IPostAttachment[];
  modelIndex: number;
  // eslint-disable-next-line react/require-default-props
  setModelIndex?: Dispatch<SetStateAction<number>>;
  className?: string;
}

function PostCarouselItem({ attachment }: { attachment: IPostAttachment }) {
  // Extension, not resourceType: posts exist whose .svg was stored as `other`.
  if (attachment.fileName.toLowerCase().endsWith('.svg')) {
    return <SvgViewer url={attachment.url} alt={attachment.fileName} />;
  }

  switch (attachment.resourceType) {
    case AttachmentType.image:
      return (
        <CarouselItem className="h-full">
          <img
            src={attachment.url}
            alt={attachment.fileName}
            className="w-full h-full rounded-md object-contain"
            loading="lazy"
            decoding="async"
          />
        </CarouselItem>
      );
    case AttachmentType.model:
      return (
        <CarouselItem className="h-full">
          <Suspense
            fallback={<Skeleton className="w-full h-full rounded-md" />}
          >
            <Viewer modelURL={attachment.url} modelColor="green" />
          </Suspense>
        </CarouselItem>
      );

    case AttachmentType.youtube: {
      const embedUrl = getYouTubeEmbedUrl(attachment.url);
      return (
        <CarouselItem className="h-full">
          <iframe
            title={attachment._id}
            src={embedUrl || attachment.url}
            className="w-full h-full rounded-md"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </CarouselItem>
      );
    }
    default:
      return <NotSupportedCarouselItem attachment={attachment} />;
  }
}

export default function PostDetailCarousel({
  attachments,
  modelIndex,
  setModelIndex,
  className,
}: PostDetailCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) return;
    if (!setModelIndex) return;

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());

    api.on('select', () => {
      setCurrentSlide(api.selectedScrollSnap());
      setModelIndex(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    });
  }, [api, setModelIndex]);

  const scrollPrev = useCallback(() => {
    if (!api) return;
    if (!setModelIndex) return;
    api.scrollPrev();
    setModelIndex(api.selectedScrollSnap());
  }, [api, setModelIndex]);

  const scrollNext = useCallback(() => {
    if (!api) return;
    if (!setModelIndex) return;
    api?.scrollNext();
    setModelIndex(api.selectedScrollSnap());
  }, [api, setModelIndex]);

  useEffect(() => {
    if (modelIndex) {
      if (modelIndex !== undefined && modelIndex !== -1) {
        api?.scrollTo(modelIndex);
      }
    } else {
      api?.scrollTo(0);
    }
  }, [modelIndex, attachments, api]);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full max-w-3xl mx-auto py-2 md:py-8 text-center">
      <Carousel setApi={setApi} className="w-full">
        <CarouselContent className={cn('h-[380px]', className)}>
          {attachments?.map((attachment) => (
            <PostCarouselItem attachment={attachment} key={attachment._id} />
          ))}
        </CarouselContent>
      </Carousel>
      <p className="text-sm text-muted-foreground mt-2">
        {attachments[currentSlide]?.fileName}
      </p>
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          size="icon"
          className="rounded-full bg-white/80 backdrop-blur-sm transition-opacity"
          onClick={scrollPrev}
          disabled={!canScrollPrev}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">Previous slide</span>
        </Button>
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
          {attachments?.map((attachment, index) => (
            <Button
              key={attachment._id}
              variant={currentSlide === index ? 'default' : 'outline'}
              size="icon"
              // The dot stays 8px; the pseudo-element is the thumb target.
              className="relative h-2 w-2 rounded-full p-0 before:absolute before:-inset-1.5 before:content-['']"
              onClick={() => api?.scrollTo(index)}
            >
              <span className="sr-only">Go to slide {index + 1}</span>
            </Button>
          ))}
        </div>
        <p>
          {currentSlide + 1}/{attachments?.length}
        </p>
        <Button
          variant="outline"
          size="icon"
          className="rounded-full bg-white/80 backdrop-blur-sm transition-opacity"
          onClick={scrollNext}
          disabled={!canScrollNext}
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">Next slide</span>
        </Button>
      </div>
    </div>
  );
}
